import { isResumable, SURFACE } from '../../app/resume';
import { MATCH_STATE } from '../../domain/machine';
import type { MatchRow } from '../../types';

/** One of the engine's own §2.5 surface names (`UiSessionRow.surface` is a plain string). */
export type SurfaceHint = (typeof SURFACE)[keyof typeof SURFACE];

/**
 * B-8 — **state-driven surface switching** for the in-match takeover shell (design spec §2.4).
 *
 * The middle band renders exactly one surface, and the choice is a total function of the match
 * row — never navigation state. §2.4: the halftime hub is *"presented from `Match.state`, never
 * navigated to"*, and the same discipline covers every B-8 presentation: the engine's row is the
 * only authority on where the user is, so a kill/relaunch reconstructs the surface from the same
 * inputs `resolveResume` reads (SES-3). The one exception the design carves out — a locked match
 * *visiting* Discovery, which is navigation, not a phase change — is B-9/B-10's to add **on top
 * of** this default, exactly as `defaultSurfaceFor` is the engine's default under `uiSession`
 * hints.
 *
 * Pure and DOM-free so the Node suite drives every arm (§12's grammar: the decisions are
 * Node-provable even where the rendering is not).
 */

/**
 * The middle band's presentation. `left` = the match is no longer the takeover's to show —
 * §2.2's boundary: `fullTime` (and everything after it) is out-of-match **by engine law**
 * (`FullTime` clears `activeMatch`; `isResumable` is preMatch ∨ isInMatch), so the shell hands
 * off to the tab world rather than rendering anything here.
 */
export type TakeoverSurface = 'preKickoff' | 'discovery' | 'board' | 'breakHub' | 'left';

export function takeoverSurfaceFor(match: MatchRow): TakeoverSurface {
  // The engine's own resumability predicate IS the takeover boundary (§2.1/§2.2) — reused, not
  // re-derived, so the shell selector at launch and the hand-off mid-session cannot disagree.
  if (!isResumable(match)) return 'left';
  if (match.state === MATCH_STATE.preMatch) return 'preKickoff';
  if (match.state === MATCH_STATE.halftime) return 'breakHub';
  return match.capturePhase === 'locked' ? 'board' : 'discovery';
}

/**
 * The `uiSession` hint recorded on surface entry (§2.2: *"`resume.recordSurface(...)` is written
 * on surface entry/exit"*). The strings are the engine's own `SURFACE` constants where one
 * matches — `captureSurfaceFor` falls back safely for unknown hints (baseline §4.9), but a hint
 * the engine already understands round-trips to the exact surface rather than to a fallback.
 *
 * `preKickoff` records the capture surface the match will open onto (scenario 6: discovery),
 * matching what B-7's Begin wrote — the state is still `preMatch` (StartCapture never starts the
 * clock), so `activeMatch` + state, not the hint, is what makes resume land pre-kickoff.
 * `left` records nothing: `FullTime` cleared `activeMatch`, so no hint can bring a relaunch
 * back here (the Needs-finishing queue owns that path, §2.2).
 */
export function surfaceHintFor(surface: TakeoverSurface, match: MatchRow): SurfaceHint | null {
  switch (surface) {
    case 'preKickoff':
      return match.capturePhase === 'locked' ? SURFACE.board : SURFACE.discovery;
    case 'discovery':
      return SURFACE.discovery;
    case 'board':
      return SURFACE.board;
    case 'breakHub':
      return SURFACE.halftimeHub;
    case 'left':
      return null;
  }
}

// ── B-31 — the break's one navigation param (design spec §21.2) ───────────────

/**
 * The single key `uiSession.surfaceParams` carries for the takeover: **is the adjustments
 * workflow open at the break?**
 *
 * §21.2: `workflowOpen` is the only piece of workflow state a remount loses that the card row
 * cannot rebuild (the *step* is db-derived — `useWorkflowB` reopens a final card on the card and a
 * draft with entries on review). Mirroring it through `recordSurface`'s **existing** 4-argument
 * form is what makes §2.5's *"relaunch re-renders the workflow from rows"* true of the navigation
 * as well as the data. No engine addition: `recordSurface(matchId, surface, surfaceParams, now)`
 * and `resolveResume`'s returned `surfaceParams` both already exist and were simply unused.
 */
export const BREAK_WORKFLOW_PARAM = 'workflowOpen';

/**
 * What the surface-recording effect writes — and the reason it takes the **hint** rather than a
 * bare boolean.
 *
 * §21.2's obligation 2 says the state-driven default must win, and the reset effect drops the
 * boolean whenever the surface is no longer the break. But an effect that runs in the *same*
 * commit as that reset still sees the pre-reset value for one render, so a writer taking a bare
 * boolean could stamp `workflowOpen: true` beside a `discovery` hint at the second-half whistle.
 * Keying the param on the hint makes that state **unconstructible** rather than merely unlikely:
 * off the break hub the params are empty, which is also §26's *"the param dropped"* after the 2H
 * whistle, stated as a value rather than as a timing hope.
 */
export function breakSurfaceParamsB(
  hint: SurfaceHint,
  workflowOpen: boolean,
): Record<string, unknown> {
  return hint === SURFACE.halftimeHub ? { [BREAK_WORKFLOW_PARAM]: workflowOpen } : {};
}

/**
 * The read-back half, at launch. Strictly `=== true`: a hint row is untrusted input (it survives
 * app versions and an archive import), and every other shape — absent, `null`, the string
 * `'true'`, a number — means *the hub's default*, which is the safe answer in every case.
 */
export function breakWorkflowOpenFrom(surfaceParams: Record<string, unknown>): boolean {
  return surfaceParams[BREAK_WORKFLOW_PARAM] === true;
}
